"use client";

import React from "react";

import Image from "next/image";
import { createPortal } from "react-dom";
import { AnimatePresence, useMotionValue, useReducedMotion, useSpring } from "framer-motion";

import { cn } from "@/lib/utils";
import { MotionDiv } from "@/components/global/utils/motionTags";
import { EASE_OUT } from "@/components/global/utils/motionTiming";
import { useIsMobile } from "@/hooks/useIsMobile";

import soundOff from "@/public/bastian-keller/sound-off.svg";
import soundOn from "@/public/bastian-keller/sound-on.svg";

/**
 * The film is never opened on its own. The hero's backdrop is already playing
 * it, looped and cropped to fill, so a click here only toggles its sound — the
 * band stays where it is and the film keeps running underneath.
 */

/**
 * Follow spring — stiff enough that the pill reads as attached to the pointer,
 * damped well past oscillation so it only ever trails.
 */
const FOLLOW_SPRING = { stiffness: 650, damping: 45, mass: 0.35 } as const;

/** The pill itself — the same object whether it is parked or riding the cursor. */
function Pill({ label, muted }: { label: string; muted: boolean }) {
  return (
    <span className="flex items-center rounded-full border border-solid border-white/20 p-[0.5rem]">
      <span className="flex items-center justify-center gap-[1rem] rounded-full bg-[#D9F9B9] px-[4rem] py-[2rem]">
        <Image
          className="!h-[2.75rem] !w-[2.75rem]"
          src={muted ? soundOff : soundOn}
          alt=""
        />
        <span className="font-geist text-[2.5rem] leading-[1.5] font-medium whitespace-nowrap text-black/80">{label}</span>
      </span>
    </span>
  );
}

export default function HeroShowreel({
  videoRef,
  unmuteLabel = "Ton einschalten",
  muteLabel = "Ton ausschalten",
  className,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  unmuteLabel?: string;
  muteLabel?: string;
  className?: string;
}) {
  const prefersReducedMotion = useReducedMotion();
  const reduceMotion = prefersReducedMotion === true;

  /*
    With a fine pointer the pill replaces the cursor: the whole band is the hit
    area, the native cursor is hidden over it and the pill rides the pointer.
    Coarse pointers have no cursor to replace and no hover to show it in, so the
    pill is parked in the middle of the screen instead.
  */
  const isCoarse = useIsMobile({ strategy: "pointer" });
  const fine = isCoarse === false;

  const [muted, setMuted] = React.useState(true);
  const [mounted, setMounted] = React.useState(false);
  const [hovering, setHovering] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, FOLLOW_SPRING);
  const springY = useSpring(y, FOLLOW_SPRING);
  // a trailing cursor is the one part of this that is motion for its own sake,
  // so reduced motion gets the raw pointer position instead
  const cursorX = reduceMotion ? x : springX;
  const cursorY = reduceMotion ? y : springY;

  React.useEffect(() => setMounted(true), []);

  const label = muted ? unmuteLabel : muteLabel;

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setMuted(video.muted);
    // a click is a user gesture, so playback with sound is allowed here
    if (video.paused) void video.play().catch(() => {});
  };

  const track = (event: React.MouseEvent) => {
    x.set(event.clientX);
    y.set(event.clientY);
  };

  const handleEnter = (event: React.MouseEvent) => {
    // snap the spring to the entry point — otherwise the pill scales up wherever
    // the pointer last left and then flies across the screen to meet it
    x.jump(event.clientX);
    y.jump(event.clientY);
    springX.jump(event.clientX);
    springY.jump(event.clientY);
    setHovering(true);
  };

  const handleLeave = () => {
    setHovering(false);
    setPressed(false);
  };

  return (
    <div className={className}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={!muted}
        onClick={toggleSound}
        onMouseEnter={handleEnter}
        onMouseMove={track}
        onMouseLeave={handleLeave}
        onPointerDown={() => setPressed(true)}
        onPointerUp={() => setPressed(false)}
        className={cn(
          "block h-full w-full appearance-none border-0 bg-transparent p-0",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D9F9B9] focus-visible:outline-offset-[-1rem]",
          fine && "cursor-none",
        )}
      />

      {/*
        Parked pill for coarse pointers, centred in the band. It sits inside the
        band rather than fixed to the viewport so it only ever lies over the
        film — a fixed pill was showing over the hero plate as soon as the band's
        top edge scrolled into view.
      */}
      {isCoarse === true && (
        <button
          type="button"
          onClick={toggleSound}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 appearance-none border-0 bg-transparent p-0">
          <Pill
            label={label}
            muted={muted}
          />
        </button>
      )}

      {/*
        The riding pill is drawn into `document.body`. It has to be `fixed` to
        track viewport coordinates, and a `fixed` child would still be clipped or
        re-stacked by any ancestor that creates a stacking context — which the
        hero's plate and the sections after it do.
      */}
      {mounted &&
        fine &&
        createPortal(
          <AnimatePresence>
            {hovering && (
              <MotionDiv
                key="showreel-cursor"
                aria-hidden
                className="pointer-events-none fixed top-0 left-0 z-[200]"
                style={{ x: cursorX, y: cursorY }}>
                {/*
                  Centring runs through framer rather than Tailwind's -translate
                  utilities: framer writes the whole `transform` string, so a
                  utility on the same element would be overwritten by the scale.
                */}
                <MotionDiv
                  initial={{ x: "-50%", y: "-50%", scale: 0, opacity: 0 }}
                  animate={{
                    x: "-50%",
                    y: "-50%",
                    scale: pressed ? 0.94 : 1,
                    opacity: 1,
                  }}
                  exit={{ x: "-50%", y: "-50%", scale: 0, opacity: 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.35, ease: EASE_OUT }}>
                  <Pill
                    label={label}
                    muted={muted}
                  />
                </MotionDiv>
              </MotionDiv>
            )}
          </AnimatePresence>,
          document.body,
        )}

    </div>
  );
}
